<script lang="ts">
  import { onMount } from 'svelte';
  import FoireAuxQuestions from './FoireAuxQuestions.svelte';
  import FormulaireDemandeSimplifiee from './FormulaireDemandeSimplifiee.svelte';

  interface Props {
    titre?: string;
    description?: string;
    mode?: 'autonome';
    origine: string;
    urlBase?: string;
    cacheLesLiensDeRetour?: boolean;
    siretAidant?: string;
  }

  let {
    titre = 'Protégez rapidement votre organisation des cyberattaques',
    description = 'Profitez d’un premier diagnostic cyber gratuit accompagné par un Aidant cyber et recevez 6 recommandations prioritaires à mettre en place pour améliorer la cybersécurité de votre organisation.',
    mode = undefined,
    origine,
    urlBase = '',
    cacheLesLiensDeRetour = false,
    siretAidant = undefined,
  }: Props = $props();

  let campagne = $state('');
  onMount(() => {
    const parametres = new URLSearchParams(window.location.search);
    campagne = parametres.get('mtm_campaign') ?? '';
  });

  let origineComplète = $derived(campagne ? `${origine};${campagne}` : origine);
</script>

<div class="demande-diagnostic" class:autonome={mode === 'autonome'}>
  <div class="explications">
    <hgroup>
      <h2>{titre}</h2>
      <p>{description}</p>
    </hgroup>
    <ol>
      <li>Complétez et envoyez votre demande.</li>
      <li>Un aidant cyber vous contacte rapidement.</li>
      <li>Réalisez votre diagnostic (en visio ou sur site – 1h).</li>
      <li>Recevez 6 recommandations prioritaires pour commencer à agir.</li>
    </ol>
    <p class="texte-mention-xs">Ce diagnostic n'est pas adapté aux particuliers et micro-entreprises.</p>
  </div>

  <div class="formulaire">
    <FormulaireDemandeSimplifiee {mode} origine={origineComplète} {urlBase} {cacheLesLiensDeRetour} {siretAidant} />
  </div>
  <details>
    <summary>
      <lab-anssi-icone nom="questionnaire-line"></lab-anssi-icone>
      Questions fréquentes
    </summary>
    <div class="faq">
      <h3 class="fr-h6">Questions les plus fréquentes</h3>
      <FoireAuxQuestions {urlBase} />
    </div>
  </details>
</div>

<style lang="scss">
  @use '../../../assets/styles/responsive' as *;

  .demande-diagnostic {
    display: grid;
    grid-template-areas:
      'explications'
      'formulaire'
      'faq';
    background-color: var(--background-alt-blue-france);
    padding: 40px 16px;
    gap: 24px;

    &.autonome {
      background-color: white;
      box-shadow: 0 2px 6px 0 rgba(0, 0, 18, 0.16);
    }

    @include a-partir-de(md) {
      padding: 40px;
    }

    @include a-partir-de(lg) {
      grid-template-areas:
        'explications formulaire'
        'faq faq';
    }

    .explications {
      grid-area: explications;

      hgroup {
        h2 {
          margin-bottom: 16px;
        }

        p {
          margin-bottom: 32px;
        }
      }
    }

    ol {
      display: flex;
      flex-direction: column;
      gap: 16px;
      counter-reset: li;
      list-style: none;
      padding: 0;

      li {
        display: flex;
        gap: 12px;

        &::before {
          flex: 0 0 16px;
          align-self: flex-start;
          height: 16px;
          line-height: 1rem;
          text-align: center;
          font-weight: bold;
          border-radius: 4px;
          padding: 4px;
          background-color: var(--artwork-major-blue-france);
          color: var(--text-inverted-grey);
          content: counter(li);
          counter-increment: li;
          box-sizing: content-box;
        }
      }
    }

    .formulaire {
      grid-area: formulaire;
      background-color: var(--background-default-grey);
      padding: 40px;
      border: 1px solid var(--border-default-grey);
    }

    details {
      grid-area: faq;

      summary {
        padding: 8px 16px 8px 12px;
        border-radius: 8px;
        width: max-content;
        display: flex;
        gap: 4px;

        &:hover {
          background-color: rgb(from var(--artwork-major-blue-france) r g b / 4%);
          cursor: pointer;
        }

        &:active {
          background-color: rgb(from var(--artwork-major-blue-france) r g b / 8%);
          cursor: pointer;
        }

        &::marker {
          content: '';
        }

        &::-webkit-details-marker {
          display: none;
        }
      }

      .faq {
        background-color: var(--background-default-grey);
        padding: 48px 16px;
        margin-top: 32px;
      }
    }
  }
</style>
